import { cn } from "@/lib/utils";
import React from "react";

interface Segment {
  value: number;
  color: string;
}

interface ProgressCircleProps
  extends Omit<React.SVGProps<SVGSVGElement>, "value"> {
  segments: Segment[];
  radius?: number;
  strokeWidth?: number;
  showAnimation?: boolean;
  children?: React.ReactNode;
}

const ProgressCircleManySegments = React.forwardRef<
  SVGSVGElement,
  ProgressCircleProps
>(
  (
    {
      segments = [],
      radius = 32,
      strokeWidth = 6,
      showAnimation = true,
      className,
      children,
      ...props
    }: ProgressCircleProps,
    forwardedRef,
  ) => {
    const normalizedRadius = radius - strokeWidth / 2;
    const circumference = normalizedRadius * 2 * Math.PI;

    let accumulatedOffset = 0;

    return (
      <div
        className={cn("relative")}
        role="progressbar"
        aria-label="Progress circle"
        data-segments={JSON.stringify(segments)}
      >
        <svg
          ref={forwardedRef}
          width={radius * 2}
          height={radius * 2}
          viewBox={`0 0 ${radius * 2} ${radius * 2}`}
          className={cn("-rotate-90 transform", className)}
          {...props}
        >
          <circle
            r={normalizedRadius}
            cx={radius}
            cy={radius}
            strokeWidth={strokeWidth}
            fill="transparent"
            stroke="lightgray"
          />

          {segments.map((segment, index) => {
            const strokeDasharray = `${circumference} ${circumference}`;
            const strokeDashoffset = accumulatedOffset;

            accumulatedOffset += (circumference * segment.value) / 100;

            return (
              <circle
                key={index}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
                strokeWidth={strokeWidth}
                fill="transparent"
                stroke={segment.color}
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                className={cn(
                  "transition-all ease-linear",
                  showAnimation &&
                    "transform-gpu transition-all duration-300 ease-in-out",
                )}
              />
            );
          })}
        </svg>
        <div
          className={cn("absolute inset-0 flex items-center justify-center")}
        >
          {children}
        </div>
      </div>
    );
  },
);

ProgressCircleManySegments.displayName = "ProgressCircleManySegments";

export { ProgressCircleManySegments, type ProgressCircleProps };
